import { Alert, Linking, Platform } from "react-native";
import HttpRoutingService from "./httpRoutingService"
import DocumentPicker, { isErrorWithCode, pick, types } from "@react-native-documents/picker";
import ReactNativeBlobUtil from "react-native-blob-util";

const AndroidMimes = Object.freeze({
  allFiles: "*/*",
  audio: "audio/*",
  csv: "text/csv",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  images: "image/*",
  pdf: "application/pdf",
  plainText: "text/plain",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  video: "video/*",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  zip: "application/zip",
} as const);

const IOSMimes = Object.freeze({
  allFiles: "public.item",
  audio: "public.audio",
  csv: "public.comma-separated-values-text",
  doc: "com.microsoft.word.doc",
  docx: "org.openxmlformats.wordprocessingml.document",
  images: "public.image",
  pdf: "com.adobe.pdf",
  plainText: "public.plain-text",
  ppt: "com.microsoft.powerpoint.ppt",
  pptx: "org.openxmlformats.presentationml.presentation",
  video: "public.movie",
  xls: "com.microsoft.excel.xls",
  xlsx: "org.openxmlformats.spreadsheetml.sheet",
  zip: "public.zip-archive",
} as const);
type AndroidMimeType = (typeof AndroidMimes)[keyof typeof AndroidMimes];
type IOSMimeType = (typeof IOSMimes)[keyof typeof IOSMimes];

type MimeTypeArray<Platform extends "android" | "ios"> =
  Platform extends "android" ? AndroidMimeType[] : IOSMimeType[];

type FilePickerOptions<Platform extends "android" | "ios"> = {
  mimeTypes?: MimeTypeArray<Platform>;
  base64?: boolean;
  multiSelection?: boolean;
};
class commonService {

  currencyStandard(currencySymbol: string, value: string) {
    const num = Number(value);
    const absValue = Math.abs(num);
    const formatted = currencySymbol == '$'
      ? absValue.toLocaleString('en-US', { currency: 'USD', maximumFractionDigits: 2, minimumFractionDigits: 2 })
      : absValue?.toLocaleString('en-IN', { currency: 'INR', maximumFractionDigits: 2, minimumFractionDigits: 2 });

    return num < 0 ? `-${currencySymbol}${formatted}` : `${currencySymbol}${formatted}`;
  }

  getStateName(countryName: string): any {
    return HttpRoutingService?.getMethod('countries/' + countryName)
  }


  imageConversion(imageUrl: string) {
    let formattedUrl;
    if (!imageUrl?.includes('base64') && !imageUrl?.includes(',')) {
      let path = imageUrl.slice(0, imageUrl.lastIndexOf('.'));
      let originalExtension = imageUrl.slice(imageUrl.lastIndexOf('.') + 1);
      if ((Platform.OS === "ios" && originalExtension !== "webp" && originalExtension !== "gif") || (Platform.OS === "android" && originalExtension !== "webp")) {
        formattedUrl = path + '.' + "webp";
      }
    }
    return formattedUrl ? formattedUrl : imageUrl
  }
  async getBase64String(fileUri: string) {
    return await ReactNativeBlobUtil.fs.readFile(fileUri, "base64");
  };

  async pickFile<P extends "android" | "ios">(
    {
      mimeTypes,
      multiSelection = false,
      base64 = false,
    }: FilePickerOptions<P>
  ) {
    try {
      const result: any = await pick({
        type: mimeTypes ?? [types.allFiles],
        allowMultiSelection: multiSelection,
      });

      if (result) {
        if (base64) {
          for (let i = 0; i < result.length; i++) {
            let uri =
              Platform.OS === "android"
                ? result[i].uri
                : decodeURIComponent(result[i].uri.replace("file://", ""));

            const base64String = await this.getBase64String(uri);
            result[i].base64 = base64String;
          }
        }

        return multiSelection ? result : result[0];
      }
    } catch (err) {
      if (isErrorWithCode(err)) {
        return { message: "Operation canceled by user." };
      }
      return { message: "Something went wrong. Please try selecting a file again." };
    }
  }

  async openFile({ isUri, file, type }: { file?: string, isUri: boolean, type?: string }) {

    if (isUri) {
      if (Platform.OS === 'android')
        ReactNativeBlobUtil.android.actionViewIntent(
          file!!,
          type!!,
        )
      else
        ReactNativeBlobUtil.ios.openDocument(file!!)

    } else {
      const supported = await Linking.canOpenURL(file!!);
      if (supported) {
        try {
          Linking.openURL(file!!);
        } catch (error) {
          console.error("Failed to open PDF in viewer:", error);
        }
      } else {
        Alert.alert(
          "No Suitable App Found",
          "There is no app on your device that can open this PDF.",
          [
            {
              text: "Cancel",
              style: "cancel",
            },
            {
              text: "OK",
              onPress: () => {
                if (Platform.OS === "android") {
                  // Navigate to Google Play Store
                  Linking.openURL("market://details?id=com.adobe.reader").catch(() =>
                    Linking.openURL("https://play.google.com/store/apps/details?id=com.adobe.reader")
                  );
                } else if (Platform.OS === "ios") {
                  // Navigate to Apple App Store
                  Linking.openURL(
                    "https://apps.apple.com/us/app/adobe-acrobat-reader-pdf-maker/id469337564"
                  );
                }
              },
            },
          ],
          { cancelable: false }
        );
      }
    }
  };
}

const CommonService = new commonService()
export default CommonService